import React, { useState, useEffect } from 'react';

const AddLoginModal = ({ show, onHide, onAdd, onEdit, editData }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [user, setUser] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    role: 'staff', // Default role
    isActive: true
  });

  useEffect(() => {
    if (editData) {
      // When editing, we usually don't want to show the hashed password
      setUser({ ...editData, password: '' });
    } else {
      setUser({ name: '', email: '', password: '', phoneNumber: '', role: 'staff', isActive: true });
    }
    setErrorMessage("");
  }, [editData, show]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setErrorMessage("");

    try {
      if (editData) {
        // Prepare data for update (including the ID)
        const updatePayload = { 
          id: editData._id, // Backend usually expects 'id' in the body for POST /update
          ...user 
        };
        await onEdit(updatePayload);
      } else {
        // Logic for Create
        if (!user.password || user.password.length < 6) {
          throw new Error("Password must be at least 6 characters long.");
        }
        // This calls handleCreateUser in the parent, which uses userService.createUser
        await onAdd(user); 
      }
      onHide();
    } catch (error) {
      // Extract specific error message from Axios response
      const errorMsg = error.response?.data?.message || error.message || "Operation failed.";
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}>
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
          
          {errorMessage && (
            <div className="position-absolute w-100 px-4" style={{ top: '10px', zIndex: 1100 }}>
              <div className="alert alert-danger shadow-sm border-0 py-2 small d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {errorMessage}
              </div>
            </div>
          )}

          <div className="modal-header border-0 pt-4 px-4">
            <div>
              <h5 className="fw-bold mb-0">{editData ? 'Edit User Login' : 'Create New Login'}</h5>
              <small className="text-muted">Manage system access credentials.</small>
            </div>
            <button type="button" className="btn-close" onClick={onHide} disabled={isUploading}></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body px-4 py-3">
              
              {/* Profile Preview Circle */}
              <div className="text-center mb-4">
                <img 
                  src={`https://ui-avatars.com/api/?name=${user.name || 'User'}&background=002147&color=fff&size=128`} 
                  alt="Avatar" 
                  className="rounded-circle border shadow-sm"
                  style={{ width: '80px', height: '80px' }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">FULL NAME</label>
                <input 
                  type="text" className="form-control" required
                  disabled={isUploading}
                  placeholder="e.g. John Doe"
                  value={user.name} 
                  onChange={(e) => setUser({...user, name: e.target.value})}
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted">EMAIL ADDRESS</label>
                  <input 
                    type="email" className="form-control" required
                    disabled={isUploading}
                    placeholder="john@vps.com"
                    value={user.email} 
                    onChange={(e) => setUser({...user, email: e.target.value})}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted">PHONE NUMBER</label>
                  <input 
                    type="text" className="form-control" 
                    disabled={isUploading}
                    placeholder="10-digit number"
                    value={user.phoneNumber} 
                    onChange={(e) => setUser({...user, phoneNumber: e.target.value})} 
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted">PASSWORD</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    required={!editData} // Password only required for new users
                    placeholder={editData ? "Leave blank to keep same" : "Min 6 characters"}
                    disabled={isUploading}
                    value={user.password} 
                    onChange={(e) => setUser({...user, password: e.target.value})} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted">USER ROLE</label>
                  <select 
                    className="form-select"
                    value={user.role}
                    disabled={isUploading}
                    onChange={(e) => setUser({...user, role: e.target.value})}
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              {editData && (
                <div className="form-check form-switch mt-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="userStatus"
                    checked={user.isActive}
                    onChange={(e) => setUser({...user, isActive: e.target.checked})}
                  />
                  <label className="form-check-label small fw-bold text-muted" htmlFor="userStatus">
                    ACCOUNT ACTIVE
                  </label>
                </div>
              )}
            </div>

            <div className="modal-footer border-0 pb-4 px-4 pt-0">
              <button type="button" className="btn btn-light px-4 fw-bold text-muted border-0" onClick={onHide} disabled={isUploading}>Discard</button>
              <button type="submit" className="btn btn-dark px-5 fw-bold shadow-sm" disabled={isUploading}>
                {isUploading ? <span className="spinner-border spinner-border-sm"></span> : (editData ? 'Update Account' : 'Create Account')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddLoginModal;